import React, { useState, useEffect } from 'react';
import { restoreSnapshot } from '../services/rdsService';
import { saveRestoredInstance } from '../services/dynamoService';
import { listVPCs, listSecurityGroups, listDBSubnetGroups } from '../services/ec2Service';

const RestoreModal = ({ snapshot, onClose, onRestore, awsCredentials }) => {
  const generateSimpleInstanceName = (snapshotId) => {
    // Extract base name from snapshot, remove common prefixes and clean up
    let baseName = snapshotId
      .replace(/^rds:/, '')
      .replace(/^(prod|dev|staging|test)-?/, '')
      .replace(/[^a-zA-Z0-9-]/g, '-') // Replace invalid chars with hyphens
      .replace(/-{2,}/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    // Keep only first meaningful part (before timestamp patterns)
    const parts = baseName.split('-');
    baseName = parts[0] || 'db';
    
    // Ensure baseName is valid and not too long
    if (baseName.length > 15) {
      baseName = baseName.substring(0, 15);
    }
    
    // Must start with letter
    if (!/^[a-zA-Z]/.test(baseName)) {
      baseName = 'db' + baseName;
    }
    
    // Create simple timestamp (YYYYMMDD-HHMM)
    const now = new Date();
    const timestamp = now.getFullYear().toString() +
                     (now.getMonth() + 1).toString().padStart(2, '0') +
                     now.getDate().toString().padStart(2, '0') +
                     now.getHours().toString().padStart(2, '0') +
                     now.getMinutes().toString().padStart(2, '0');
    
    const result = `restored-${baseName}-${timestamp}`;
    
    // Ensure total length is within AWS limits (max 63 characters)
    if (result.length > 63) {
      const maxBaseLength = 63 - 'restored--'.length - timestamp.length;
      const shortBaseName = baseName.substring(0, maxBaseLength);
      return `restored-${shortBaseName}-${timestamp}`;
    }
    
    return result;
  };

  const [config, setConfig] = useState({
    instanceIdentifier: generateSimpleInstanceName(snapshot.DBSnapshotIdentifier),
    instanceClass: 'db.t3.micro',
    publiclyAccessible: true,
    multiAZ: false,
    storageType: 'gp2',
    securityGroupIds: '',
    subnetGroupName: '',
    selectedVpc: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // AWS Resource states
  const [vpcs, setVpcs] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [dbSubnetGroups, setDbSubnetGroups] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  useEffect(() => {
    loadAWSResources();
  }, []);

  useEffect(() => {
    if (config.selectedVpc) {
      loadSecurityGroupsForVpc(config.selectedVpc);
    }
  }, [config.selectedVpc]);

  const loadAWSResources = async () => {
    try {
      setLoadingResources(true);
      console.log('Loading AWS resources for restore modal...');
      
      const [vpcsData, subnetGroupsData] = await Promise.all([
        listVPCs(awsCredentials),
        listDBSubnetGroups(awsCredentials)
      ]);
      
      setVpcs(vpcsData);
      setDbSubnetGroups(subnetGroupsData);
      
      // Load security groups for all VPCs initially
      const allSecurityGroups = await listSecurityGroups(undefined, awsCredentials);
      setSecurityGroups(allSecurityGroups);
      
      console.log(`Loaded ${vpcsData.length} VPCs, ${subnetGroupsData.length} DB subnet groups, ${allSecurityGroups.length} security groups`);
    } catch (err) {
      console.error('Error loading AWS resources:', err);
      setError('Failed to load AWS resources: ' + err.message);
    } finally {
      setLoadingResources(false);
    }
  };

  const loadSecurityGroupsForVpc = async (vpcId) => {
    try {
      console.log(`Loading security groups for VPC: ${vpcId}`);
      const vpcSecurityGroups = await listSecurityGroups(vpcId, awsCredentials);
      setSecurityGroups(vpcSecurityGroups);
      
      // Clear selected security group when VPC changes
      setConfig(prev => ({ ...prev, securityGroupIds: '' }));
    } catch (err) {
      console.error('Error loading security groups for VPC:', err);
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      setError(null);

      const restoreConfig = {
        instanceClass: config.instanceClass,
        publiclyAccessible: config.publiclyAccessible,
        multiAZ: config.multiAZ,
        storageType: config.storageType,
        securityGroupIds: config.securityGroupIds ? [config.securityGroupIds] : [],
        subnetGroupName: config.subnetGroupName || undefined,
      };

      const restoredInstance = await restoreSnapshot(
        snapshot.DBSnapshotIdentifier,
        config.instanceIdentifier,
        restoreConfig,
        awsCredentials
      );

      await saveRestoredInstance({
        ...restoredInstance,
        snapshotId: snapshot.DBSnapshotIdentifier
      }, awsCredentials);

      onRestore();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aws-modal-overlay">
      <div className="aws-modal">
        <div className="aws-modal-header">
          <h2 className="aws-modal-title">
            Restore DB snapshot: {snapshot.DBSnapshotIdentifier}
          </h2>
        </div>
        
        <div className="aws-modal-body">
          {error && (
            <div className="aws-alert aws-alert-error aws-mb-3">
              <strong>Restore failed:</strong> {error}
            </div>
          )}

          <div className="aws-alert aws-alert-info aws-mb-3">
            <strong>Source snapshot:</strong> {snapshot.DBSnapshotIdentifier} ({snapshot.Engine})
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">DB instance identifier *</label>
            <input
              type="text"
              className="aws-form-input"
              value={config.instanceIdentifier}
              onChange={(e) => setConfig({ ...config, instanceIdentifier: e.target.value })}
              placeholder="Enter unique identifier for the new instance"
            />
            <div style={{ fontSize: '12px', color: '#687078', marginTop: '4px' }}>
              Must start with a letter, contain only letters, numbers, and hyphens. Max 63 characters.
            </div>
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">DB instance class</label>
            <select
              className="aws-form-select"
              value={config.instanceClass}
              onChange={(e) => setConfig({ ...config, instanceClass: e.target.value })}
            >
              <option value="db.t3.micro">db.t3.micro (1 vCPU, 1 GB RAM)</option>
              <option value="db.t3.small">db.t3.small (2 vCPU, 2 GB RAM)</option>
              <option value="db.t3.medium">db.t3.medium (2 vCPU, 4 GB RAM)</option>
              <option value="db.m5.large">db.m5.large (2 vCPU, 8 GB RAM)</option>
              <option value="db.m5.xlarge">db.m5.xlarge (4 vCPU, 16 GB RAM)</option>
            </select>
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">VPC (optional)</label>
            <select
              className="aws-form-select"
              value={config.selectedVpc}
              onChange={(e) => setConfig({ ...config, selectedVpc: e.target.value })}
              disabled={loadingResources}
            >
              <option value="">Select VPC (all security groups will be shown)</option>
              {vpcs.map(vpc => (
                <option key={vpc.vpcId} value={vpc.vpcId}>
                  {vpc.vpcId} - {vpc.name} ({vpc.cidrBlock}) {vpc.isDefault ? '(Default)' : ''}
                </option>
              ))}
            </select>
            {loadingResources && <div style={{ fontSize: '12px', color: '#687078' }}>Loading VPCs...</div>}
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">Security group</label>
            <select
              className="aws-form-select"
              value={config.securityGroupIds}
              onChange={(e) => setConfig({ ...config, securityGroupIds: e.target.value })}
              disabled={loadingResources}
            >
              <option value="">Select security group (optional)</option>
              {securityGroups
                .filter(sg => !config.selectedVpc || sg.vpcId === config.selectedVpc)
                .map(sg => (
                  <option key={sg.groupId} value={sg.groupId}>
                    {sg.groupId} - {sg.groupName} ({sg.description})
                  </option>
                ))}
            </select>
            {loadingResources && <div style={{ fontSize: '12px', color: '#687078' }}>Loading security groups...</div>}
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">DB subnet group</label>
            <select
              className="aws-form-select"
              value={config.subnetGroupName}
              onChange={(e) => setConfig({ ...config, subnetGroupName: e.target.value })}
              disabled={loadingResources}
            >
              <option value="">Select DB subnet group (optional)</option>
              {dbSubnetGroups
                .filter(sg => !config.selectedVpc || sg.vpcId === config.selectedVpc)
                .map(sg => (
                  <option key={sg.name} value={sg.name}>
                    {sg.name} - {sg.description} (VPC: {sg.vpcId})
                  </option>
                ))}
            </select>
            {loadingResources && <div style={{ fontSize: '12px', color: '#687078' }}>Loading subnet groups...</div>}
          </div>

          <div className="aws-form-checkbox">
            <input
              type="checkbox"
              id="publicAccess"
              checked={config.publiclyAccessible}
              onChange={(e) => setConfig({ ...config, publiclyAccessible: e.target.checked })}
            />
            <label htmlFor="publicAccess" className="aws-form-label aws-mb-0">
              Public access - Allow connections from the internet
            </label>
          </div>

          <div className="aws-form-checkbox">
            <input
              type="checkbox"
              id="multiAZ"
              checked={config.multiAZ}
              onChange={(e) => setConfig({ ...config, multiAZ: e.target.checked })}
            />
            <label htmlFor="multiAZ" className="aws-form-label aws-mb-0">
              Multi-AZ deployment - For high availability
            </label>
          </div>
        </div>

        <div className="aws-modal-footer">
          <button
            className="aws-button aws-button-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="aws-button"
            onClick={handleRestore}
            disabled={loading || !config.instanceIdentifier.trim()}
          >
            {loading ? 'Restoring database...' : 'Restore DB instance'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestoreModal;